import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Color, Size, Icon } from '@americanexpress/aqx-components';
// removed sanitizer import
import './__styles__/index.scss';

export class Tooltip extends React.Component {
  static checkSSRWindow(windowType) {
    return windowType === 'undefined'
      ? {
          winWidth: 0,
          winHeight: 0
        }
      : {
          winWidth: window.innerWidth,
          winHeight: window.innerHeight
        };
  }

  constructor(props) {
    super(props);
    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.onResize = this.onResize.bind(this);
    this.checkMobileMode = this.checkMobileMode.bind(this);
    this.calculateArrowClass = this.calculateArrowClass.bind(this);
    this.styles = this.props.styles;

    this.minimumTopMargin = 60;
    const initialWindowSize = Tooltip.checkSSRWindow(typeof window);

    this.state = {
      visible: false,
      mobileMode: false,
      swingUpward: false,
      swingHeight: 0,
      oversize: false, // refers to tooltips that are too long to render from the current position.
      winWidth: initialWindowSize.winWidth,
      winHeight: initialWindowSize.winHeight,
      body: {
        display: 'none'
      }
    };
  }

  calculatePopupPosition(
    node,
    winWidth,
    winHeight,
    popupWidth,
    minMargin,
    arrowSize
  ) {
    let rect = node ? node.getBoundingClientRect() : { width: 0, height: 0 };
    let absX = rect.x;
    let xOffset = -2 * arrowSize;
    let yOffset = rect.height;
    let showArrow = true;

    // Determine if tooltip will be placed too far to the right.
    const absRightBorder = absX + node.offsetWidth + popupWidth + minMargin;
    if (absRightBorder > winWidth) {
      xOffset = winWidth - absRightBorder - minMargin - 2 * arrowSize;
    }

    // Determine if tooltip will be placed too far to the left.
    if (absX + xOffset < minMargin) {
      xOffset = arrowSize;
    }

    // Custom function to modify tooltip x/y placement
    if (typeof this.props.getCustomizedPosition === 'function') {
      const { newX, newY } = this.props.getCustomizedPosition(
        rect,
        yOffset,
        xOffset
      );
      xOffset = newX;
      yOffset = newY;
    }

    // If our close button overlaps the arrow, hide the arrow.
    if (popupWidth + xOffset < minMargin * 2 + arrowSize) {
      showArrow = false;
    }

    return {
      xOffset,
      yOffset,
      showArrow
    };
  }

  componentWillUnmount() {
    this.removeResizeListener();
    this.removeClickListener();
  }

  onResize() {
    this.setState({
      winWidth: window.innerWidth,
      winHeight: window.innerHeight,
      mobileMode: this.checkMobileMode(window)
    });
  }

  setPopupPosition() {
    const { winWidth, winHeight } = this.state;
    const { popupWidth, minMargin, arrowSize } = this.props;
    const { xOffset, yOffset, showArrow } = this.calculatePopupPosition(
      this.tooltipRef,
      winWidth,
      winHeight,
      popupWidth,
      minMargin,
      arrowSize
    );

    this.setState({
      arrow: this.setPopupArrowStyle(
        xOffset - (this.props.centerPopupArrow ? 8 : 0),
        yOffset,
        arrowSize,
        showArrow,
        this.state
      ),
      body: this.setPopupBodyStyle(
        this.state,
        popupWidth,
        xOffset,
        yOffset,
        arrowSize
      )
    });
  }

  show() {
    this.addResizeListener();
    this.addClickListener();
    this.setState(
      {
        mobileMode: this.state.oversize || this.checkMobileMode(window),
        visible: true,
        winHeight: window.innerHeight,
        winWidth: window.innerWidth
      },
      this.setPopupPosition
    );
  }

  hide() {
    this.removeResizeListener();
    this.removeClickListener();
    this.setState(
      {
        visible: false,
        // Reset oversize + swing so that if the user scrolls
        // and re-opens the tooltip, we can recalculate the
        // appearance of the tooltip based on the new position.
        oversize: false,
        swingUpward: false,
        mobileMode: false
      },
      this.setPopupPosition
    );
  }

  addResizeListener() {
    window.addEventListener('resize', this.onResize);
  }

  removeResizeListener() {
    window.removeEventListener('resize', this.onResize);
  }

  addClickListener() {
    window.addEventListener('click', this.handleOutsideClick);
  }

  removeClickListener() {
    window.removeEventListener('click', this.handleOutsideClick);
  }

  handleOutsideClick(e) {
    if (!this.tooltipRef.contains(e.target)) {
      this.hide();
    }
  }

  checkMobileMode(window) {
    return window.innerWidth <= this.props.mobileBreakpoint;
  }

  componentDidUpdate() {
    // Using this lifecycle method is ugly, but it allows us to see the real
    // height of the tooltip, after it's been rendered on the user's screen.
    //
    // If the tooltip falls off the bottom of the screen, we first attempt
    // to make it 'swing upward'. If, after it swings upward, the top border
    // of the tooltip crosses the top of the page (e.g. 60px, the height Kodama
    // is currently using up with a banner), we then need to make it full-screen
    // (which is how the tooltip renders in mobile mode).
    if (!this.state.visible) {
      return;
    }

    let regularTooltipBoundingRect = this.regularTooltipRef
      ? this.regularTooltipRef.getBoundingClientRect()
      : { width: 0, height: 0 };
    let topEdge = regularTooltipBoundingRect.y;
    let bottomEdge =
      regularTooltipBoundingRect.y + regularTooltipBoundingRect.height;
    if (
      !this.state.oversize &&
      !this.state.swingUpward &&
      bottomEdge > this.state.winHeight
    ) {
      this.setState(
        { swingUpward: true, swingHeight: regularTooltipBoundingRect.height },
        () => this.setPopupPosition()
      );
    } else if (
      (!this.state.oversize && topEdge < 0) ||
      (topEdge > 0 && topEdge < this.minimumTopMargin)
    ) {
      this.setState({ mobileMode: true, oversize: true, swingUpward: false }, () =>
        this.setPopupPosition()
      );
    }
  }

  setPopupBodyStyle = (
    { visible, swingUpward, swingHeight },
    popupWidth,
    xOffset,
    yOffset,
    arrowSize
  ) => {
    return {
      position: 'absolute',
      display: visible ? 'block' : 'none',
      width: popupWidth,
      left: xOffset - 1.5 * arrowSize,
      top: swingUpward
        ? -swingHeight - 4 * arrowSize - yOffset
        : arrowSize - yOffset,
      marginTop: 2 * arrowSize,
      marginBottom: 2 * arrowSize
    };
  };

  setPopupArrowStyle = (
    xOffset,
    yOffset,
    arrowSize,
    showArrow,
    { swingUpward, swingHeight }
  ) => ({
    position: 'absolute',
    left: -xOffset,
    top: swingUpward ? swingHeight + 2 * arrowSize : -arrowSize,
    display: showArrow ? 'initial' : 'none'
  });

  calculateArrowClass({ swingUpward }) {
    return swingUpward
      ? this.styles.arrowAtTooltipBottom
      : this.styles.arrowAtTooltipTop;
  }

  calculateMobileClass({ visible, mobileMode }) {
    return clsx(
      this.styles.mobileTooltipWrap,
      visible && mobileMode ? this.styles.showtip : ''
    );
  }

  calculateMobileStyle = ({ visible, mobileMode }) => ({
    display: visible && mobileMode ? 'initial' : 'none'
  });

  render() {
    const Tag = this.props.pageType === 'pdp' ? 'h3' : this.props.tagName;
    const closeButtonIcon = this.props.useNewCloseIcon ? (
      <Icon
        directory="dls"
        imageName="dls-icon-close"
        $size={Size.XS}
        theme={{
          color: Color.BrightBlue
        }}
      />
    ) : (
      'X'
    );
    return (
      <span className={this.styles.tooltip} ref={ref => (this.tooltipRef = ref)}>
        <span
          className={clsx(this.styles.tooltipDefaultText, this.props.className)}
          onClick={() => this.show()}
        >
          {this.props.clickableElement || (
            <Tag
              className="clickableText"
              dangerouslySetInnerHTML={{ __html: this.props.clickableText }}
            />
          )}
        </span>

        {/* body of regular tooltip */}
        <span
          className={clsx(
            this.styles.openGcpTooltip,
            this.styles.tooltipPopup,
            this.props.increasedPadding && this.styles.increasedPadding,
            this.styles.fireFoxMarginRemove
          )}
          style={this.state.body}
        >
          <span
            className={this.calculateArrowClass(this.state)}
            style={this.state.arrow}
          >
            <span />
          </span>
          <span
            className={clsx(this.styles.tooltipCloseButton, {
              [this.styles.icon]: this.props.useNewCloseIcon
            })}
            onClick={() => this.hide()}
          >
            {closeButtonIcon}
          </span>
          <span
            ref={ref => (this.regularTooltipRef = ref)}
            dangerouslySetInnerHTML={{ __html: this.props.tooltipPopupText }}
          />
        </span>

        {/* body of mobile tooltip */}
        <div
          className={this.calculateMobileClass(this.state)}
          style={this.calculateMobileStyle(this.state)}
        >
          <span>
            <span
              dangerouslySetInnerHTML={{ __html: this.props.tooltipPopupText }}
            />
          </span>
          {/* Desktop tooltip close button */}
          <span
            className={clsx(this.styles.tooltipCloseButton, this.styles.mobile)}
            onClick={() => this.hide()}
          >
            {closeButtonIcon}
          </span>
        </div>
      </span>
    );
  }
}

Tooltip.propTypes = {
  clickableElement: PropTypes.node,
  clickableText: PropTypes.string,
  tooltipPopupText: PropTypes.string.isRequired,
  className: PropTypes.string,
  popupWidth: PropTypes.number,
  arrowSize: PropTypes.number,
  minMargin: PropTypes.number,
  mobileBreakpoint: PropTypes.number,
  styles: PropTypes.objectOf(PropTypes.string),
  tagName: PropTypes.string,
  useNewCloseIcon: PropTypes.bool,
  centerPopupArrow: PropTypes.bool,
  increasedPadding: PropTypes.bool,
  getCustomizedPosition: PropTypes.func
};

Tooltip.defaultProps = {
  mobileBreakpoint: 434,
  popupWidth: 430,
  arrowSize: 10,
  minMargin: 10,
  children: undefined,
  useNewCloseIcon: false,
  centerPopupArrow: false,
  increasedPadding: false,
  styles: {
    arrowAtTooltipBottom: 'arrowAtTooltipBottom',
    arrowAtTooltipTop: 'arrowAtTooltipTop',
    bottomPosition: 'bottomPosition',
    mobile: 'mobile',
    mobileTooltipWrap: 'mobileTooltipWrap',
    openGcpTooltip: 'openGcpTooltip',
    showtip: 'showtip',
    tooltip: 'tooltip',
    tooltipCloseButton: 'tooltipCloseButton',
    tooltipDefaultText: 'tooltipDefaultText',
    tooltipPopup: 'tooltipPopup',
    topPosition: 'topPosition',
    tooltipPopupText: 'tooltipPopupText',
    icon: 'icon',
    increasedPadding: 'increasedPadding',
    fireFoxMarginRemove: 'fireFoxMarginRemove '
  },
  tagName: 'span',
  getCustomizedPosition: undefined
};
